/** Console v0.5: start-up and routing. main.ts hands over here when
 * /api/me says console_v05 (test only until the release). Each screen is a
 * module registered in SCREENS; until a screen is rebuilt it shows a
 * placeholder that links to the current console. */

import { installShortcuts, openSearch, renderFrame, setInboxBadge } from "./frame.js";
import { accountGate, inPageTarget, openCount, pageName, parseRoute, routePath, visibleRoute } from "./routes.js";
import type { Route, V05Me } from "./routes.js";
import { apiRequest, setOnTermsRequired } from "../api.js";
import { card, h, legalFooter, linkButton, pageHead } from "./ui.js";

export interface ScreenContext {
  me: V05Me;
  route: Route;
  root: HTMLElement; // the content column
  navigate: (path: string) => void;
  timeZone: string;
  /** Set the top bar's breadcrumb once the screen knows it (e.g. an organisation's name). */
  setCrumbs: (crumbs: ReadonlyArray<{ label: string; href?: string }>) => void;
}

/** A screen fills ctx.root; it may return a clean-up for when the person moves on. */
export type Screen = (ctx: ScreenContext) => void | (() => void);

/** Rebuilt screens, by route kind. Each screen PR adds its own entry. */
export const SCREENS: Partial<Record<Route["kind"], Screen>> = {};

/** App-wide hooks a screen module can fill (the recorder: its banner on every page, and the sign-out check). */
export const HOOKS: {
  banner?: () => HTMLElement | null;
  beforeSignOut?: (proceed: () => void) => void;
} = {};

/** Re-reads /api/me's counts and updates the Review inbox badge (A: after approve, send back, release, review). */
export async function refreshCounts(me: V05Me): Promise<void> {
  try {
    const fresh = await apiRequest<V05Me>("GET", "/api/me", "Preekie couldn't refresh the counts.");
    me.counts = fresh.counts ?? null;
    setInboxBadge(openCount(me));
  } catch {
    /* the badge keeps its last value; the next load reads it again */
  }
}

/** Redraws the banner slot above the content (e.g. a recording started or its audio finished uploading). */
export function updateBanner(): void {
  const slot = document.querySelector(".pk5-banner-slot");
  if (!slot) return;
  const b = HOOKS.banner?.() ?? null;
  slot.replaceChildren(...(b ? [b] : []));
}

export interface StartOptions {
  root: HTMLElement;
  me: V05Me;
  onSignOut: () => void;
  timeZone: string;
  /** Called once with the in-app navigate, before the first screen (e.g. to start the recorder host). */
  onReady?: (navigate: (path: string) => void) => void;
}

function placeholder(ctx: ScreenContext): void {
  const name = pageName(ctx.route);
  const classic = `${routePath(ctx.route)}${routePath(ctx.route).includes("?") ? "&" : "?"}console=classic`;
  ctx.root.append(
    pageHead(name, null),
    card(
      undefined,
      h("p", null, "This screen is being rebuilt for the new console. Until it's ready, use the current console."),
      linkButton("Open the current console", classic, "secondary")
    ),
    legalFooter()
  );
}

function reports(ctx: ScreenContext): void {
  // Brief s.8: Reports content isn't in this release.
  ctx.root.append(pageHead("Reports", "Reports are coming soon."), legalFooter());
}

function notFound(ctx: ScreenContext): void {
  ctx.root.append(
    pageHead("Page not found", "There's nothing at this address. It may have moved, or you may not have access to it."),
    linkButton("Go to Home", "/", "primary"),
    legalFooter()
  );
}

function setUpPending(ctx: ScreenContext): void {
  ctx.root.append(pageHead("Your account is being set up", "Preekie opens once your account's payment is set up."), legalFooter());
}

function suspended(ctx: ScreenContext): void {
  const mail = h("a", "pk5-link", "hello@preekie.com");
  mail.href = "mailto:hello@preekie.com";
  ctx.root.append(
    pageHead("Your account is suspended", "Your team can't use Preekie until the account is reactivated. Nothing in it has been deleted."),
    h("p", null, "Questions: ", mail, "."),
    legalFooter()
  );
}

export function startV05(opts: StartOptions): void {
  const { root, me } = opts;
  const signOut = () => (HOOKS.beforeSignOut ? HOOKS.beforeSignOut(opts.onSignOut) : opts.onSignOut());
  root.hidden = false;
  root.classList.add("pk5-app");
  // Terms to accept mid-session (Privacy Policy v1.2): start again, and start-up shows the acceptance screen. A recording under way
  // still gets the browser's leave-page question first.
  setOnTermsRequired(() => location.reload());
  let cleanup: (() => void) | null = null;

  const show = (route: Route) => {
    cleanup?.();
    cleanup = null;
    const shown = visibleRoute(route, me);
    let crumbs: ReadonlyArray<{ label: string; href?: string }> = [{ label: pageName(shown) }];
    const draw = () => renderFrame(root, { me, route: shown, crumbs, navigate, onSignOut: signOut, onSwitchAccount: switchAccount, timeZone: opts.timeZone });
    const content = draw();
    updateBanner();
    document.title = `${pageName(shown)} · Preekie`;
    const ctx: ScreenContext = {
      me,
      route: shown,
      root: content,
      navigate,
      timeZone: opts.timeZone,
      setCrumbs: (c) => {
        crumbs = c;
        // Redraw only the top bar's crumbs, keeping the screen as it is.
        const bar = root.querySelector(".pk5-crumbs");
        if (!bar) return;
        bar.replaceChildren();
        c.forEach((x, i) => {
          if (i > 0) bar.append(h("span", "pk5-crumb-sep", " / "));
          if (x.href) {
            const a = h("a", "pk5-crumb", x.label);
            a.href = x.href;
            bar.append(a);
          } else bar.append(h("span", "pk5-crumb pk5-crumb-current", x.label));
        });
        bar.lastElementChild?.setAttribute("aria-current", "page");
      },
    };
    const gate = accountGate(me);
    if (gate === "pending_payment") setUpPending(ctx);
    else if (gate === "suspended") suspended(ctx);
    else if (shown.kind === "not-found") notFound(ctx);
    else if (shown.kind === "reports") reports(ctx);
    else {
      const screen = SCREENS[shown.kind];
      if (screen) cleanup = screen(ctx) || null;
      else placeholder(ctx);
    }
  };

  /** POST /api/session/account (A s.17), then start again in that account on Home (its /api/me is different). */
  function switchAccount(accountId: string): void {
    void apiRequest("POST", "/api/session/account", "Preekie couldn't switch account.", { account_id: accountId })
      .then(() => location.assign("/"))
      .catch((err: unknown) => {
        const content = root.querySelector(".pk5-content");
        if (content) content.prepend(h("div", "pk5-notice pk5-notice-alert", err instanceof Error ? err.message : "Preekie couldn't switch account."));
      });
  }

  function navigate(path: string): void {
    history.pushState(null, "", path);
    const url = new URL(path, location.origin);
    show(parseRoute(url.pathname, url.search));
    document.getElementById("pk5-title")?.focus();
  }

  // In-app links move without a reload; anything else (new tab, other origin, ?console=classic) is left alone.
  root.addEventListener("click", (ev) => {
    if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
    const a = (ev.target as Element | null)?.closest("a");
    if (!a || a.target || a.hasAttribute("download")) return;
    // A place on this page (the skip link): move focus there; it's not a new screen.
    const spot = inPageTarget(a.getAttribute("href") ?? "", location);
    if (spot !== null) {
      const el = document.getElementById(spot);
      if (el) {
        ev.preventDefault();
        if (el.tabIndex < 0 && !el.hasAttribute("tabindex")) el.tabIndex = -1;
        el.focus();
      }
      return;
    }
    const url = new URL(a.href, location.href);
    if (url.origin !== location.origin || url.searchParams.get("console") === "classic" || url.pathname.startsWith("/api/")) return;
    if (url.protocol === "mailto:") return;
    ev.preventDefault();
    navigate(url.pathname + url.search);
  });
  window.addEventListener("popstate", () => show(parseRoute(location.pathname, location.search)));
  installShortcuts(() =>
    openSearch({ me, route: parseRoute(location.pathname, location.search), crumbs: [], navigate, onSignOut: signOut, timeZone: opts.timeZone })
  );

  opts.onReady?.(navigate);
  const first = parseRoute(location.pathname, location.search);
  const shown = visibleRoute(first, me);
  if (shown.kind !== "not-found") {
    // Keep a screen's own query (e.g. /inbox?item=…), unless the route already writes one.
    const path = routePath(shown);
    const keep = shown.kind === first.kind && !path.includes("?") ? location.search : "";
    history.replaceState(null, "", path + keep);
  }
  show(first);
}
